import { TFunction } from 'i18next';
import {
  SellerConnectAdminAnswer,
  SellerConnectAdminSubmission,
  SellerConnectChatMessage,
  SellerConnectChatRole,
  SellerConnectQuestionRequirement,
  SellerConnectSeller,
} from 'models/SellerConnectEngine';
import { getFormattedDate } from 'dateUtils';
import { getArrayLength, getHasElementsInArray, getUserFullName } from 'utils';
import { SellersTableData } from './components/SellersTable/data';
import { EMPTY_CELL_VALUE } from 'data/global';
import { SellerDetailsRow } from './components/SellerDetailsCard/SellerDetailsCard.component';
import { IM_STATUSES_WITH_PDF } from './data';

export const getSellerBusinessName = (
  business: SellerConnectSeller['business'],
) => business?.businessName || business?.tradingName || business?.entityName;

export const getSellerLocation = (business: SellerConnectSeller['business']) =>
  [business?.state, business?.postcode].filter(Boolean).join(' ');

export const getSellersTableRows = (
  sellers: SellerConnectSeller[] = [],
): SellersTableData[] =>
  sellers.map(
    ({
      _id,
      sceSellerId,
      seller,
      business,
      screening,
      intakeStatus,
      submittedAt,
    }) => ({
      _id,
      submissionId: sceSellerId,
      seller: getUserFullName(seller?.firstName, seller?.lastName),
      business: getSellerBusinessName(business) || EMPTY_CELL_VALUE,
      industry: business?.industryLabel || EMPTY_CELL_VALUE,
      location: getSellerLocation(business) || EMPTY_CELL_VALUE,
      revenueRange: business?.revenueRange || EMPTY_CELL_VALUE,
      intakeVia: screening?.mode ?? null,
      intakeStatus: intakeStatus ?? null,
      submittedAt,
    }),
  );

export const getSellerFormattedDate = (date: string | null) =>
  date ? getFormattedDate(new Date(date)) : EMPTY_CELL_VALUE;

export const getSellerAnswersByRequirement = (
  submission: SellerConnectAdminSubmission,
  requirement: SellerConnectQuestionRequirement,
) =>
  (submission?.screening?.blocks ?? [])
    .flatMap(({ answers }) => answers)
    .filter((answer) => answer.requirement === requirement);

export const getSellerAnswerRows = (
  answers: SellerConnectAdminAnswer[],
): SellerDetailsRow[] =>
  answers.map(({ label, display }) => ({
    label,
    value: display || EMPTY_CELL_VALUE,
  }));

export const getIsChatTranscript = (
  transcript: SellerConnectAdminSubmission['screening']['transcript'],
) => Array.isArray(transcript);

export const getSellerDrawerDates = (
  submission: SellerConnectAdminSubmission,
  t: TFunction,
) => {
  const intakeDate = getSellerIntakeDate(submission);
  const latestImWithPdf = getSellerLatestImWithPdf(submission);

  return [
    intakeDate &&
      t('sellerDrawerIntakeOn', {
        date: getSellerFormattedDate(intakeDate),
      }),
    latestImWithPdf &&
      t('sellerDrawerImEditedOn', {
        date: getSellerFormattedDate(latestImWithPdf.createdAt),
      }),
  ]
    .filter(Boolean)
    .join(' · ');
};

export const getTextTranscriptMessages = (transcript: string) =>
  transcript
    .split('\n')
    .reduce<SellerConnectChatMessage[]>((messages, line) => {
      const [, speaker, content] = line.match(/^(agent|user):\s*(.*)$/i) ?? [];

      if (speaker)
        return [
          ...messages,
          { role: speaker.toLowerCase() as SellerConnectChatRole, content },
        ];

      if (!line.trim() || !getHasElementsInArray(messages)) return messages;

      const last = messages[getArrayLength(messages) - 1];

      return [
        ...messages.slice(0, -1),
        { ...last, content: `${last.content}\n${line}` },
      ];
    }, []);

export const getTranscriptMessages = (
  transcript: SellerConnectAdminSubmission['screening']['transcript'],
) =>
  getIsChatTranscript(transcript)
    ? (transcript as SellerConnectChatMessage[])
    : getTextTranscriptMessages(transcript as string);

export const getSellerIntakeDate = (submission: SellerConnectAdminSubmission) =>
  submission?.screening?.completedAt ||
  submission?.screening?.startedAt ||
  submission?.submittedAt ||
  submission?.createdAt;

export const getHasSellerTranscript = (
  transcript: SellerConnectAdminSubmission['screening']['transcript'],
) =>
  getIsChatTranscript(transcript)
    ? getHasElementsInArray(transcript as SellerConnectChatMessage[])
    : !!transcript;

export const getSellerLatestImWithPdf = (
  submission: SellerConnectAdminSubmission,
) =>
  submission?.imVersions?.find(({ status }) =>
    IM_STATUSES_WITH_PDF.includes(status),
  );
